import type { ScheduleConfig, TargetingConfig } from './types';

const NEW_VISITOR_KEY = 'cms_lf_returning_visitor';

/** Substring match, same semantics ContentTypeEditor's users are told to expect ("comma-separated
 * substrings") — not a full glob/regex engine, deliberately simple. */
function matchesAny(patterns: string[] | null | undefined, url: string): boolean {
  if (!patterns || patterns.length === 0) return false;
  return patterns.some((pattern) => pattern.trim() !== '' && url.includes(pattern.trim()));
}

function isMobile(): boolean {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isReturningVisitor(): boolean {
  const seen = localStorage.getItem(NEW_VISITOR_KEY) === '1';
  if (!seen) {
    try {
      localStorage.setItem(NEW_VISITOR_KEY, '1');
    } catch {
      // Storage can be unavailable (private browsing, quota) — targeting just degrades to "new".
    }
  }
  return seen;
}

/** @param visitorCountry Server-resolved (see api.ts's ActiveFormsResult) — null means either
 * geo-IP isn't configured on the backend or the visitor's IP couldn't be resolved, in which case a
 * country-restricted form simply doesn't match (fails closed, not open). */
export function matchesTargeting(targeting: TargetingConfig, visitorCountry: string | null): boolean {
  const url = window.location.href;

  if (matchesAny(targeting.urlExclude, url)) return false;
  if (targeting.urlInclude && targeting.urlInclude.length > 0 && !matchesAny(targeting.urlInclude, url)) {
    return false;
  }

  if (targeting.devices && targeting.devices.length > 0) {
    const device = isMobile() ? 'MOBILE' : 'DESKTOP';
    if (!targeting.devices.includes(device)) return false;
  }

  if (targeting.visitorType === 'NEW' && isReturningVisitor()) return false;
  if (targeting.visitorType === 'RETURNING' && !isReturningVisitor()) return false;

  if (targeting.countries && targeting.countries.length > 0) {
    if (!visitorCountry || !targeting.countries.includes(visitorCountry)) return false;
  }

  if (targeting.schedule && !matchesSchedule(targeting.schedule)) return false;

  return true;
}

function matchesSchedule(schedule: ScheduleConfig): boolean {
  const now = new Date();

  if (schedule.startAt && now < new Date(schedule.startAt)) return false;
  if (schedule.endAt && now > new Date(schedule.endAt)) return false;

  const hasDayRestriction = !!schedule.daysOfWeek && schedule.daysOfWeek.length > 0;
  const hasTimeRestriction = !!schedule.startTime || !!schedule.endTime;
  if (!hasDayRestriction && !hasTimeRestriction) return true;

  const parts = zonedParts(now, schedule.timezone);

  if (hasDayRestriction && !schedule.daysOfWeek!.includes(parts.isoWeekday)) return false;

  if (hasTimeRestriction) {
    const minutesNow = parts.hour * 60 + parts.minute;
    const startMinutes = schedule.startTime ? toMinutes(schedule.startTime) : 0;
    const endMinutes = schedule.endTime ? toMinutes(schedule.endTime) : 24 * 60 - 1;
    if (minutesNow < startMinutes || minutesNow > endMinutes) return false;
  }

  return true;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Reads the current day/hour/minute either in the visitor's own local time (timezone unset) or a
 * fixed IANA zone, via Intl — no date library needed for just this. */
function zonedParts(now: Date, timezone: string | null): { isoWeekday: number; hour: number; minute: number } {
  if (!timezone) {
    // getDay() is 0=Sunday..6=Saturday; ScheduleConfig uses the ISO 1=Monday..7=Sunday convention.
    const day = now.getDay();
    return { isoWeekday: day === 0 ? 7 : day, hour: now.getHours(), minute: now.getMinutes() };
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const weekdayShort = parts.find((p) => p.type === 'weekday')?.value ?? '';
    // Midnight in some zones renders as hour "24" with hour12: false — normalize to 0.
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0') % 24;
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
    const isoWeekday = WEEKDAY_SHORT_TO_ISO[weekdayShort] ?? (now.getDay() === 0 ? 7 : now.getDay());
    return { isoWeekday, hour, minute };
  } catch {
    // Invalid timezone id — degrade to the visitor's own local time rather than throwing.
    const day = now.getDay();
    return { isoWeekday: day === 0 ? 7 : day, hour: now.getHours(), minute: now.getMinutes() };
  }
}

const WEEKDAY_SHORT_TO_ISO: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};
